import { httpResource, HttpResourceRequest } from '@angular/common/http'
import { ChangeDetectionStrategy, Component, effect, input, model, signal, untracked } from '@angular/core'
import { toObservable, toSignal } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { FormValueControl } from '@angular/forms/signals'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { ErrorStateMatcher } from '@angular/material/core'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { debounceTime, filter } from 'rxjs'
import {
  osmAutocompleteApiKey,
  searchUrl,
  StreetSearchHints,
  streetSearchParams,
  streetSuggestionLabel,
} from '../street-autocomplete.config'

export interface StreetAutocompleteResult {
  street?: string
  housenumber?: string
  postcode?: string
  city?: string
}

/**
 * Straße mit Vorschlagsliste; die Auswahl fuellt Hausnummer, PLZ und Ort gleich mit.
 *
 * **Seit 2026-10 nur noch der Strassenname** (Backend `docs/claude/adressen.md`). Die Hausnummer
 * steht im eigenen Feld daneben, das der Aufrufer selbst rendert. `houseNumber` ist deshalb
 * Pflicht-Model: ohne `[houseNumber]` baut die Vorlage nicht, und kein Formular erfasst mehr eine
 * Strasse ohne Hausnummernfeld.
 *
 * **`FormValueControl`**: der Wert liegt in einem `model()`. Damit laesst sich die Komponente an
 * einen Signal-Forms-Feldbaum binden (`[formField]="fg.strasse"`) **und** ohne jedes Formular
 * verwenden (`[(value)]="meinSignal"`). Wer sie aus einer verbliebenen Reactive-`FormGroup`
 * heraus binden muss, wickelt das Control mit `compatForm(...)` ein — siehe das PHM-Formular der
 * Selfservice-App.
 */
@Component({
  selector: 'lib-street-autocomplete',
  imports: [FormsModule, MatAutocompleteModule, MatInputModule, MatIconModule],
  templateUrl: './street-autocomplete.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './street-autocomplete.component.scss',
})
export class StreetAutocompleteComponent implements FormValueControl<string | null> {
  readonly value = model<string | null>(null)

  /**
   * Pflichtfeld-Markierung (Stern, rote Kontur, „Bitte eintragen.“). An einem echten Signal-Forms-Feld
   * setzt `[formField]` sie von selbst aus der Schema-Regel `required(...)`.
   */
  readonly required = input(false)

  /**
   * Dieselbe Markierung fuer Aufrufer hinter `compatForm(...)`. Dort kommt ein Reactive-
   * `Validators.required` NICHT als `required` an: der Compat-Layer reicht Fehler und Status durch,
   * aber keine Metadaten, und Schema-Regeln auf Compat-Pfaden verbietet schon der Typ
   * (`supportsRules`). Ohne diesen Eingang fehlte dem Strassenfeld im Selfservice der Stern, waehrend
   * die Hausnummer daneben ihn trug (2026-10-07).
   */
  readonly pflicht = input(false)

  /** Status des gebundenen Feldes, gesetzt von `[formField]` (auch hinter `compatForm`). */
  readonly invalid = input(false)
  readonly touched = input(false)

  /**
   * Rot wird das Feld nach dem Status des FORMULARS, nicht nach dem des inneren `ngModel`. Das innere
   * Control wird beim Absenden nie beruehrt (`markAllAsTouched` erreicht es nicht) und kennt die
   * Regeln des Aufrufers nicht; ohne diesen Matcher bliebe ein leeres Pflichtfeld nach „Weiter“ grau.
   */
  readonly fehlerZustand: ErrorStateMatcher = {
    isErrorState: (control) => (this.invalid() && this.touched()) || !!(control?.invalid && control.touched),
  }

  readonly houseNumber = model.required<string | null | undefined>()
  readonly postcode = model<string | undefined>(undefined)
  readonly city = model<string | undefined>(undefined)

  /**
   * Erst nach der ersten Tastatureingabe wird gesucht.
   *
   * Ersetzt das fruehere `filter(() => formControl.dirty)`: ohne diese Sperre loest schon das
   * Vorbefuellen des Feldes eine Abfrage aus — ein Suchlauf gegen eine Adresse, die der Nutzer
   * gar nicht eingegeben hat.
   */
  private readonly beruehrt = signal(false)

  /** Strasse samt Nachbarfeldern im Moment der Abfrage, siehe `constructor`. */
  readonly query = signal<{ q: string; hints: StreetSearchHints }>({ q: '', hints: {} })
  readonly suggestions = httpResource<StreetAutocompleteResult[]>(() => {
    const { q, hints } = this.query()
    if (!q) {
      return undefined
    }
    return <HttpResourceRequest>{
      url: searchUrl,
      params: streetSearchParams(q, hints),
      headers: {
        'x-api-key': osmAutocompleteApiKey,
      },
    }
  })

  /**
   * Gedrosselt wird die **Abfrage**, nicht das Feld.
   *
   * Ein `debounce` auf dem Feld selbst verzoegert den UI→Modell-Abgleich; die CVA des Inputs
   * schreibt dann den nachhinkenden Wert zurueck und getippte Zeichen verschwinden (Repo-Regel
   * „`debounce` nicht auf ein `[matAutocomplete]`-Feld legen").
   */
  readonly debouncedValue = toSignal(
    toObservable(this.value).pipe(
      filter(() => this.beruehrt()),
      debounceTime(250),
    ),
  )

  constructor() {
    // Nur das Strassenfeld loest eine Suche aus; Hausnummer, PLZ und Ort werden untracked mitgelesen,
    // sonst feuerte jede Taste in diesen Feldern eine Abfrage, deren Liste niemand sieht.
    effect(() => {
      const q = this.debouncedValue() ?? ''
      const hints = untracked(() => ({ housenumber: this.houseNumber(), postcode: this.postcode(), city: this.city() }))
      this.query.set({ q, hints })
    })
  }

  readonly label = streetSuggestionLabel

  markiereBeruehrt() {
    this.beruehrt.set(true)
  }

  applyAutocompleteSelection(suggestion: StreetAutocompleteResult) {
    // Die Strasse kommt ueber den [value] der Option ins Feld. Ein Vorschlag ohne Hausnummer laesst
    // eine schon getippte stehen.
    const hausnummer = suggestion.housenumber?.trim()
    if (hausnummer) {
      this.houseNumber.set(hausnummer)
    }
    this.postcode.set(suggestion.postcode)
    this.city.set(suggestion.city)
  }
}
